/**
 * Privacy Mode Manager
 * Coordinates privacy mode state and the dashboard pre-arm button.
 *
 * Features:
 * - Toggles privacy mode on/off (enable-only server-side; exit = new session)
 * - Dashboard pre-arm button morphs between idle and armed states
 * - Confirmation dialog when exiting privacy mode
 * - API integration to flag sessions as private
 * - Session-boundary reset: a mode switch / new session clears stale privacy
 *   UI unless the new session was itself born private (P10 pre-arm)
 * - Dashboard privacy pre-arm: toggled locally (no API call) until the user
 *   actually submits, at which point the session is born private
 *
 * Header entry/exit is owned by ChatHeaderMenu; this manager remains the
 * privacy state owner and emits `privacy:changed` for decoupled consumers.
 */

import { Tooltip } from '../utils/Tooltip.js';
import { modalService } from '../services/ModalService.js';
import { sessionManager } from '../core/SessionManager.js';
import { eventBus } from '../core/utils.js';
import page from '../vendor/page.js';

const ACTIVE_CLASS = 'header-privacy-btn--active';

export class PrivacyModeManager {
  /** Create the privacy state owner and dashboard pre-arm registry. */
  constructor() {
    this.isPrivacyMode = false;
    // Dashboard privacy pre-arm (P10) — distinct from isPrivacyMode (an actual
    // ACTIVE session flagged private). Toggled by the dashboard's own button
    // via toggleArm(); never derived from isPrivacyMode (guardrail: a
    // confirm-exit replacement session must always be born PUBLIC).
    this.isArmed = false;
    // Additional button instances registered via applyTo (e.g. the dashboard's
    // own privacy button), kept in sync with every state change.
    this.extraButtons = new Set();
    // Tooltip teardown fns keyed by their button node. The dashboard rewrites
    // its button's DOM node on every render(), so stale nodes must have their
    // tooltip listeners (and any live tooltip) cleaned up — otherwise a
    // detached node's tooltip orphans on document.body (it can never fire the
    // mouseleave that hides it).
    this._buttonTooltipCleanups = new Map();
    // Same-click-mistake tracking: reset false whenever privacy is entered,
    // flipped true the moment a message is actually sent while private. This
    // is a single-tab UI interaction — no other actor can send a message into
    // this flow — so a plain in-memory flag is a trustworthy signal; no
    // server-side verification needed for what's just an undo affordance.
    this._activitySinceEntry = false;

    this.initialize();
  }

  /**
   * Initialize event listeners and tooltip
   */
  initialize() {
    // Session-boundary reset (⚠R1 C3, P9-pre hard prerequisite): a mode switch
    // / new session must never inherit stale privacy UI from the previous
    // session. The one exception is a session BORN private (P10 dashboard
    // pre-arm) — its `session:started` payload carries `privacyMode: true`.
    eventBus.on('session:started', ({ privacyMode }) => {
      // The dashboard pre-arm (if any) has now been consumed by session
      // creation one way or the other — clear it unconditionally so the
      // dashboard button never shows a stale "armed" state on return,
      // regardless of page-transition timing (this is deterministic; the
      // page:changed hygiene below is a fallback for navigation that never
      // creates a session at all).
      if (this.isArmed) {
        this.isArmed = false;
        sessionManager.setPendingPrivacyArm(false);
      }
      if (privacyMode === true) {
        // Born-private sessions are only ever created BY a dashboard submit,
        // so their first user message is already in flight when this fires —
        // and the message:send activity listener missed it (isPrivacyMode was
        // still false at dispatch time). Treat birth as activity: the
        // same-click-mistake cancel is a mid-session-entry affordance only;
        // here the button must always go through the exit confirmation.
        this._activitySinceEntry = true;
        this._applyPrivacyState(true);
      } else if (this.isPrivacyMode) {
        this._applyPrivacyState(false);
      } else {
        this._refreshArmedUI(); // isArmed may have just changed with nothing else to do
      }
    });

    // P10 hygiene: clear a dangling pre-arm on ANY navigation away from the
    // dashboard (resume-card attach, nav-card mode switches, etc.) — those
    // paths never consume `pendingPrivacyArm` themselves, so left uncleared it
    // would sit armed for hours. The dashboard-submit path that DOES consume
    // it reads the flag synchronously before this listener's async
    // continuation can fire (see ModeOrchestrator's message:send handler).
    // `page: null` means the dashboard itself is showing — only a transition
    // to a real page counts as "leaving."
    eventBus.on('page:changed', ({ page }) => {
      if (page !== null) {
        if (this.isArmed) {
          this.isArmed = false;
          sessionManager.setPendingPrivacyArm(false);
          this._refreshArmedUI();
        }
        return;
      }
      // Dashboard is showing: any live private-session UI state is now stale.
      // Navigating home abandons the session (mirror of the reload rule —
      // router.js never saves a private session for return, and a dashboard
      // submit always resetSession()s before creating), so the dashboard must
      // always load with privacy off. The pre-arm is deliberately NOT touched
      // here: an arm only exists while the dashboard is already showing, and a
      // redundant dashboard-show must not wipe a fresh arm.
      if (this.isPrivacyMode) {
        this._applyPrivacyState(false);
      }
    });

    // Same-click-mistake tracking: any actual message send while private means
    // there's now real content that would be lost by silently reverting.
    eventBus.on('message:send', () => {
      if (this.isPrivacyMode) {
        this._activitySinceEntry = true;
      }
    });

    console.log('[PrivacyModeManager] Initialized');
  }

  /**
   * Toggle the dashboard privacy pre-arm state. Pure client-side state (no API
   * call) — pushes into SessionManager so the next dashboard-submit lazy
   * session creation can consume it.
   * @returns {boolean} The new armed state
   */
  toggleArm() {
    this.isArmed = !this.isArmed;
    sessionManager.setPendingPrivacyArm(this.isArmed);
    this._refreshArmedUI();
    return this.isArmed;
  }

  /**
   * Re-apply state to every registered button + the shared chat-input note
   * after an armed-state change (isPrivacyMode unaffected).
   * @private
   */
  _refreshArmedUI() {
    this.extraButtons.forEach((btn) => this.applyTo(btn));
    const active = this._isActiveState();
    document.querySelector('.chat-input')?.classList.toggle('chat-input--privacy', active);
    // Header title ("Private Conversation") gets a heavier weight while private
    // — the title is plain text from the descriptor pipeline, so the styling
    // hook has to live here as a state class on the header element.
    document.getElementById('mode-header')?.classList.toggle('header--privacy', active);
  }

  /**
   * Combined visual-active state: an actual private session OR a dashboard
   * pre-arm. Never conflate the two in application logic (only in display).
   * @returns {boolean}
   * @private
   */
  _isActiveState() {
    return this.isPrivacyMode || this.isArmed;
  }

  /**
   * Handle a click on the button while private. If no message has been sent
   * since privacy was entered (tracked client-side), try the same-click-mistake
   * cancel: silently revert, no modal, no session close, no navigation, full
   * history intact. Otherwise go straight to the normal exit confirmation —
   * the cancel route is never even called once real activity has occurred.
   * @private
   */
  async requestExit() {
    if (!this._activitySinceEntry) {
      const reverted = await sessionManager.tryCancelPrivacyMode();
      if (reverted) {
        this._applyPrivacyState(false);
        console.log('[PrivacyModeManager] Privacy cancelled — no activity since entry, session unchanged');
        return;
      }
    }
    this.showExitConfirmation();
  }

  /**
   * Show confirmation dialog when user tries to exit privacy mode
   */
  async showExitConfirmation() {
    const confirmed = await modalService.confirm({
      title: 'Exit Privacy Mode',
      message: 'This will end our session and this conversation will be permanently deleted.',
      confirmText: 'Continue',
      cancelText: 'Cancel'
    });

    if (confirmed) {
      this.confirmExitPrivacyMode();
    } else {
      console.log('[PrivacyModeManager] User chose to stay in privacy mode');
    }
  }

  /**
   * Enter privacy mode
   * - Flags current session as private via API
   * - Morphs the header button to its "private" state
   *
   * Fail-closed (⚠R1 P1): on ANY API failure, the privacy UI is never entered —
   * no state, no CSS class, no button morph. The two failure shapes get distinct
   * copy: a retryable network error vs a 400 on an already-closed session (where
   * nothing retroactive is possible in V1).
   */
  async enterPrivacyMode() {
    if (this.isPrivacyMode) return;

    try {
      const success = await sessionManager.setPrivacyMode();
      if (!success) {
        window.notificationManager?.showError(
          "Couldn't enable privacy mode — please try again."
        );
        return;
      }

      this._activitySinceEntry = false; // fresh entry — nothing sent yet
      this._applyPrivacyState(true);
      console.log('[PrivacyModeManager] Entered privacy mode');
    } catch (error) {
      console.error('[PrivacyModeManager] Failed to enter privacy mode:', error);
      if (error?.code === 'SESSION_NOT_ACTIVE') {
        window.notificationManager?.showError(
          "This session has already ended, so privacy mode couldn't be applied retroactively."
        );
      } else {
        window.notificationManager?.showError(
          "Couldn't reach the server — please try again."
        );
      }
      // Fail closed: no UI state change on any failure shape.
    }
  }

  /**
   * Confirm exit from privacy mode (called after user confirms dialog)
   * - Ends the current private session (closes unprocessed — privacyMode
   *   already blocks summary/extraction, so nothing to skip explicitly)
   * - Navigates to the dashboard — mirrors ActionRouter's established
   *   `return_to_dashboard`/`orientation_exit` teardown sequence exactly:
   *   endSession() → orchestrator.returnToDashboard() (mode/chat/header
   *   teardown) → page('/') (the actual page-visibility switch; `returnToDashboard()`
   *   alone only tears down mode/chat state — it does NOT change which page is
   *   shown, that's the router's job)
   * - Exits privacy mode UI state
   *
   * No replacement session is created — unlike a normal mode exit, there is
   * nothing to continue into (the whole point of exiting is that the private
   * conversation is over), so this is simpler than the old switchMode(force)
   * approach and there's no risk of a replacement session inheriting privacy.
   */
  async confirmExitPrivacyMode() {
    try {
      // Exit privacy mode UI. endSession() below also ends the session (no
      // replacement is created), so there's nothing else to reset — but doing
      // it here keeps the UI honest even if endSession fails.
      this.exitPrivacyMode();

      // endSession() does not throw on failure (network errors are swallowed
      // internally) — it resolves false when the backend didn't confirm
      // closure, so check the return value rather than relying on try/catch.
      const closed = await sessionManager.endSession();
      if (!closed) {
        window.notificationManager?.showError(
          "Couldn't end the private session — please try again."
        );
        this._applyPrivacyState(true); // the old session is still what's live
        return;
      }

      // endSession() already emitted session:ended → ModeOrchestrator.returnToDashboard()
      // (mode/chat/header teardown). page('/') drives the actual page switch —
      // same two-call sequence as ActionRouter's return_to_dashboard/orientation_exit.
      page('/');

      console.log('[PrivacyModeManager] Exited privacy mode, session ended, returning to dashboard');
    } catch (error) {
      console.error('[PrivacyModeManager] Failed to exit privacy mode:', error);
      // Re-apply the private state on error — the old session is still what's live.
      this._applyPrivacyState(true);
    }
  }

  /**
   * Exit privacy mode (UI state only)
   */
  exitPrivacyMode() {
    if (!this.isPrivacyMode) return;
    this._applyPrivacyState(false);
    console.log('[PrivacyModeManager] Exited privacy mode');
  }

  /**
   * Apply the private/idle UI state to the header button, every registered
   * extra button (dashboard), and the shared chat-input note class. Idempotent.
   * @param {boolean} active
   * @private
   */
  _applyPrivacyState(active) {
    this.isPrivacyMode = active;
    this._refreshArmedUI();
    eventBus.emit('privacy:changed', { active });
  }

  /**
   * Apply the current privacy state to a single button element (idempotent
   * state-class application). Used both for the header button and any
   * additionally-registered instance (P10 dashboard button, which is
   * re-rendered from scratch on every DashboardManager.render() call).
   * @param {HTMLElement|null|undefined} element
   */
  applyTo(element) {
    if (!element) return;
    const active = this._isActiveState();
    element.classList.toggle(ACTIVE_CLASS, active);
    element.setAttribute('aria-label', active ? 'Exit privacy mode' : 'Enter privacy mode');
    // The button stays visible in BOTH states — under the morph, feature-toggle
    // visibility and privacy-mode-active are independent axes (⚠R1 W6); only
    // `applyFeatureState` (feature-toggle--hidden) governs whether it renders
    // at all, and even then it stays visible while private (see applyFeatureState).
  }

  /**
   * Register an additional button element (e.g. the dashboard's own privacy
   * button) to be kept in sync with every future state change, and immediately
   * apply the current state to it. Call after every DashboardManager.render()
   * since that rewrites the button's DOM node.
   * @param {HTMLElement|null|undefined} element
   */
  registerButton(element) {
    if (!element) return;
    // Prune buttons whose DOM node has been replaced by a re-render: tear down
    // their tooltip (removing any orphaned instance) and forget them, so stale
    // nodes don't leak listeners or leave a stuck tooltip behind.
    for (const [node, cleanup] of this._buttonTooltipCleanups) {
      if (!node.isConnected) {
        cleanup();
        this._buttonTooltipCleanups.delete(node);
        this.extraButtons.delete(node);
      }
    }
    if (!this.extraButtons.has(element)) {
      this.extraButtons.add(element);
      // Fresh DOM node (DashboardManager rewrites it every render) — attach
      // the same tooltip as the header instance. The has() guard prevents
      // duplicate listeners if the same node is ever registered twice.
      this._buttonTooltipCleanups.set(element, this._setupTooltip(element));
    }
    this.applyTo(element);
  }

  /**
   * Attach the shared privacy tooltip (identical text + left placement on
   * both the header and dashboard button instances).
   * @param {HTMLElement} element
   * @returns {Function} Cleanup fn that removes the tooltip listeners + instance
   * @private
   */
  _setupTooltip(element) {
    return Tooltip.setup(element, {
      position: 'left',
      getText: () => (this._isActiveState() ? 'Exit privacy mode' : 'Enter privacy mode')
    });
  }

  /**
   * Check if currently in privacy mode
   * @returns {boolean}
   */
  getIsPrivacyMode() {
    return this.isPrivacyMode;
  }

}
